import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, Copy, Download, ExternalLink, Eye, FileSpreadsheet, FileText, Megaphone, Presentation, RefreshCw, RotateCcw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { PaginationFooter } from "@/components/PaginationFooter";
import { ProposalTabs } from "@/components/ProposalTabs";
import { RegisterSearch } from "@/components/RegisterSearch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { inr } from "@/lib/format";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatDateTime(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function DateTimeStack({ value }) {
  if (!value) return <span className="text-slate-400">-</span>;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return <span className="text-slate-400">-</span>;
  return (
    <div className="leading-tight">
      <div>{date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>
      <div className="mt-0.5 text-xs font-semibold text-slate-500">{date.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}</div>
    </div>
  );
}

const sentCacheKey = "__hoardexaSentProposalsCache";

export function SentProposals() {
  const nav = useNavigate();
  const [items, setItems] = useState(() => globalThis[sentCacheKey] || []);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  async function load(force = false) {
    if (!force && globalThis[sentCacheKey]) {
      setItems(globalThis[sentCacheKey]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/proposals", { params: { limit: 50 }, skipGlobalLoader: true });
      globalThis[sentCacheKey] = asArray(data);
      setItems(globalThis[sentCacheKey]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(true); }, []);

  function copyLink(url) {
    navigator.clipboard?.writeText(url);
    toast.success("Link copied");
  }

  function filenameFromResponse(response, fallback) {
    const disposition = response.headers?.["content-disposition"] || "";
    const match = disposition.match(/filename="?([^"]+)"?/i);
    return match?.[1] || fallback;
  }

  async function downloadArtifact(proposal, kind) {
    const key = `${proposal.id}:${kind}`;
    setBusyAction(key);
    try {
      const response = await api.get(`/proposals/${proposal.id}/download/${kind}`, { responseType: "blob" });
      const ext = kind === "excel" ? "xlsx" : kind === "ppt" ? "pptx" : "pdf";
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: response.headers?.["content-type"] || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameFromResponse(response, `Hoardexa_Proposal.${ext}`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Could not download proposal file");
    } finally {
      setBusyAction("");
    }
  }

  async function createCampaignFromProposal(proposal) {
    const key = `${proposal.id}:campaign`;
    setBusyAction(key);
    try {
      const { data } = await api.get(`/proposals/${proposal.id}/campaign-prefill`, { skipGlobalLoader: true });
      delete globalThis[sentCacheKey];
      nav("/campaigns/new", {
        state: {
          createCampaign: {
            ...data,
            start_date: (data.start_date || "").slice(0, 10),
            end_date: (data.end_date || "").slice(0, 10),
          },
        },
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Could not prepare campaign");
    } finally {
      setBusyAction("");
    }
  }

  async function voidProposal(proposal) {
    if (proposal.proposal_status === "void") return;
    const confirmed = window.confirm(`Void proposal #${proposal.id}? This will free hoardings blocked by this proposal.`);
    if (!confirmed) return;
    const key = `${proposal.id}:void`;
    setBusyAction(key);
    try {
      await api.post(`/proposals/${proposal.id}/void`, {}, { skipGlobalLoader: true });
      delete globalThis[sentCacheKey];
      toast.success(`Proposal #${proposal.id} voided`);
      await load(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Could not void proposal");
    } finally {
      setBusyAction("");
    }
  }

  function openProposalTarget(proposal) {
    if (proposal.linked_campaign_id) {
      nav(`/campaigns/${proposal.linked_campaign_id}`);
      return;
    }
    nav(`/proposals/${proposal.id}`);
  }

  function searchableText(proposal) {
    return [
      proposal.id,
      proposal.title,
      proposal.client_name,
      proposal.brand_name,
      proposal.campaign_name,
      proposal.linked_campaign_code,
      proposal.proposal_status,
      formatDateTime(proposal.proposal_expires_at),
      formatDateTime(proposal.created_at),
      ...asArray(proposal.links).flatMap((link) => [
        link.id,
        link.share_url,
        link.status,
        formatDateTime(link.created_at),
      ]),
    ].filter(Boolean).join(" ").toLowerCase();
  }

  function commitSearch() {
    setCommittedSearch(searchDraft.trim());
    setPage(1);
  }

  function resetView() {
    setSearchDraft("");
    setCommittedSearch("");
    setPage(1);
    setPageSize(10);
  }

  const filteredItems = useMemo(() => {
    const query = committedSearch.toLowerCase();
    if (!query) return items;
    return items.filter((proposal) => searchableText(proposal).includes(query));
  }, [items, committedSearch]);

  const pageCount = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const visibleItems = filteredItems.slice((safePage - 1) * pageSize, safePage * pageSize);
  const publicLinkCount = items.reduce((sum, item) => sum + asArray(item.links).length, 0);
  const totalOpens = items.reduce((sum, item) => sum + Number(item.open_count || 0), 0);

  return (
    <Layout
      title="Sent Proposals"
      subtitle="Generated files, shared links, opens, and client responses"
      mainClassName="bg-[#F8FAFC] p-3 sm:p-6"
    >
      <div className="hx-proposal-v2 hx-hoardings-page mx-auto w-full max-w-[1600px]">
        <div className="mb-4 flex flex-wrap items-center gap-5 rounded-xl border border-[#E2E8F0] bg-white px-5 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" style={{ minHeight: 68 }}>
          <Metric label="Proposals Sent" value={items.length} />
          <Metric label="Public Links" value={publicLinkCount} />
          <Metric label="Total Opens" value={totalOpens} />
          <div className="ml-auto">
            <Button variant="outline" onClick={() => load(true)} disabled={loading} className="h-9 rounded-lg border-[#CBD5E1] text-[#334155]">
              <RefreshCw size={15} /> Refresh
            </Button>
          </div>
        </div>

        <ProposalTabs />

        <section className="hx-inventory-register" aria-busy={loading}>
          <header>
            <div>
              <h2>Proposals Sent</h2>
              <p>{filteredItems.length} records in current view</p>
            </div>
            <div className="hx-register-controls">
              <RegisterSearch
                value={searchDraft}
                onChange={(value) => setSearchDraft(value)}
                onSearch={commitSearch}
                placeholder="Search proposal ID, client, campaign..."
                testId="sent-proposals-search"
                ariaLabel="Search proposals"
                maxLength={80}
              />
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger className="hx-register-rows">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 per page</SelectItem>
                  <SelectItem value="25">25 per page</SelectItem>
                  <SelectItem value="50">50 per page</SelectItem>
                </SelectContent>
              </Select>
              <button type="button" onClick={resetView} className="hx-reset-action">
                <RotateCcw size={13} /> Reset
              </button>
            </div>
          </header>

          <div className={`hx-mobile-hoarding-list md:hidden ${loading ? "is-refreshing" : ""}`}>
            {visibleItems.map((proposal) => (
              <ProposalMobileCard
                key={proposal.id}
                proposal={proposal}
                busyAction={busyAction}
                copyLink={copyLink}
                createCampaignFromProposal={createCampaignFromProposal}
                downloadArtifact={downloadArtifact}
                nav={nav}
                onOpenDetails={openProposalTarget}
                voidProposal={voidProposal}
              />
            ))}
            {visibleItems.length === 0 && (
              <div className="px-4 py-10 text-center">
                <div className="font-display text-base font-bold text-[#0F172A]">{loading ? "Loading proposals" : "No proposals found"}</div>
                <div className="mt-1 text-sm text-[#64748B]">Try another proposal ID, client, campaign, or link search.</div>
              </div>
            )}
          </div>

          <div className={`hx-inventory-table-wrap hidden md:block ${loading ? "is-refreshing" : ""}`}>
            <table>
              <thead>
                <tr>
                  <th className="is-hoarding">Proposal</th>
                  <th>Created At</th>
                  <th>Link Sent</th>
                  <th>Expiry</th>
                  <th className="text-right">Media</th>
                  <th className="text-right">Cost</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((proposal) => (
                  <Fragment key={proposal.id}>
                    <tr className="cursor-pointer" onClick={() => openProposalTarget(proposal)}>
                      <td>
                        <ProposalIdentity proposal={proposal} />
                      </td>
                      <td className="whitespace-nowrap text-slate-700">{formatDateTime(proposal.created_at)}</td>
                      <td>
                        <ProposalLinks proposal={proposal} copyLink={copyLink} />
                      </td>
                      <td className="whitespace-nowrap text-slate-700">
                        <DateTimeStack value={proposal.proposal_expires_at} />
                      </td>
                      <td className="text-right font-bold text-slate-900">{proposal.total_selected || 0}</td>
                      <td className="text-right font-bold text-slate-900">{inr(proposal.total_cost || 0)}</td>
                      <td>
                        <ProposalActions
                          proposal={proposal}
                          busyAction={busyAction}
                          createCampaignFromProposal={createCampaignFromProposal}
                          downloadArtifact={downloadArtifact}
                          nav={nav}
                          voidProposal={voidProposal}
                        />
                      </td>
                    </tr>
                    {proposal.latest_response && (
                      <tr className="cursor-default bg-blue-50/40 hover:bg-blue-50/40">
                        <td colSpan={7} className="py-2">
                          <LatestResponse response={proposal.latest_response} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {visibleItems.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-16 text-center">
                      <div className="font-display text-base font-bold text-[#0F172A]">{loading ? "Loading proposals" : "No proposals found"}</div>
                      <div className="mt-1 text-sm text-[#64748B]">Try another proposal ID, client, campaign, or link search.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <PaginationFooter page={safePage} pageSize={pageSize} total={filteredItems.length} onPageChange={setPage} label="proposals" />
        </section>
      </div>
    </Layout>
  );
}

function ProposalIdentity({ proposal }) {
  return (
    <div className="hx-hoarding-cell">
      <div className="min-w-0">
        <strong title={proposal.campaign_name || proposal.title}>{proposal.campaign_name || proposal.title || "Untitled proposal"}</strong>
        <span>
          <b>{proposal.id}</b>
          {proposal.client_name && <> · {proposal.client_name}</>}
          {proposal.brand_name && <> · {proposal.brand_name}</>}
        </span>
        <ProposalStatus proposal={proposal} />
        <PartnerApproval proposal={proposal} />
      </div>
    </div>
  );
}

function ProposalStatus({ proposal }) {
  if (proposal.proposal_status === "void") {
    return (
      <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-md bg-rose-50 px-2 py-0.5 text-[11px] font-black text-rose-700">
        <XCircle size={12} /> Voided {proposal.voided_at ? formatDateTime(proposal.voided_at) : ""}
      </span>
    );
  }
  return (
    <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-black text-emerald-700">
      <CheckCircle2 size={12} /> Active
    </span>
  );
}

function PartnerApproval({ proposal }) {
  if (!proposal.partner_approval_status || proposal.partner_approval_status === "none") return null;
  return (
    <span className={`mt-1 inline-flex w-fit items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-black ${proposal.partner_approval_status === "approved" ? "bg-emerald-50 text-emerald-700" : proposal.partner_approval_status === "rejected" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
      {proposal.partner_approval_status === "approved" ? <CheckCircle2 size={12} /> : <Clock size={12} />}
      Partner approval: {proposal.partner_approval_status}
    </span>
  );
}

function ProposalLinks({ proposal, copyLink }) {
  const links = asArray(proposal.links);
  if (links.length === 0) return <span className="text-xs font-semibold text-slate-400">No public link</span>;
  const link = links[0];
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${link.status === "active" ? "border-blue-200 bg-blue-50 text-blue-700" : "border-slate-200 bg-slate-50 text-slate-600"}`}>{link.status}</span>
        {links.length > 1 && <span className="text-xs font-semibold text-slate-500">+{links.length - 1} more</span>}
      </div>
      <div className="mt-1 flex min-w-0 items-center gap-2">
        <button type="button" onClick={(event) => { event.stopPropagation(); copyLink(link.share_url); }} className="shrink-0 rounded-md border border-slate-200 bg-white p-1.5 text-slate-600 hover:text-blue-600" aria-label="Copy link">
          <Copy size={13} />
        </button>
        <a href={link.share_url} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()} className="shrink-0 rounded-md border border-slate-200 bg-white p-1.5 text-slate-600 hover:text-blue-600" aria-label="Open link">
          <ExternalLink size={13} />
        </a>
      </div>
    </div>
  );
}

function LatestResponse({ response }) {
  return (
    <div className="text-sm">
      <span className="font-semibold text-slate-900">Latest response: {response.response_type}</span>
      <span className="ml-2 text-slate-600">{response.client_name || "Client"} · {formatDateTime(response.created_at)}</span>
      {response.message && <span className="ml-2 text-slate-500">{response.message}</span>}
    </div>
  );
}

function ProposalActions({ proposal, busyAction, createCampaignFromProposal, downloadArtifact, nav, voidProposal }) {
  const isVoid = proposal.proposal_status === "void";
  const hasCampaign = Boolean(proposal.linked_campaign_id);
  return (
    <div className="flex flex-wrap items-center gap-1.5" onClick={(event) => event.stopPropagation()}>
      {hasCampaign ? (
        <Button onClick={() => nav(`/campaigns/${proposal.linked_campaign_id}`)} className="h-8 rounded-lg bg-[#2563EB] px-2.5 text-white hover:bg-[#1D4ED8]">
          <Eye size={14} /> View
        </Button>
      ) : (
        <Button onClick={() => createCampaignFromProposal(proposal)} disabled={isVoid || busyAction === `${proposal.id}:campaign` || proposal.partner_approval_status === "pending" || proposal.partner_approval_status === "rejected"} className="h-8 rounded-lg bg-[#2563EB] px-2.5 text-white hover:bg-[#1D4ED8] disabled:opacity-60">
          <Megaphone size={14} /> Campaign
        </Button>
      )}
      {!hasCampaign && (
        <Button variant="outline" onClick={() => voidProposal(proposal)} disabled={isVoid || busyAction === `${proposal.id}:void`} className="h-8 rounded-lg border-rose-200 px-2.5 text-rose-700 hover:bg-rose-50 disabled:opacity-60">
          <XCircle size={14} /> Void
        </Button>
      )}
      <Button variant="outline" onClick={() => downloadArtifact(proposal, "pdf")} disabled={busyAction === `${proposal.id}:pdf`} className="h-8 rounded-lg border-[#CBD5E1] px-2.5 text-[#334155]">
        <FileText size={14} /> PDF
      </Button>
      <Button variant="outline" onClick={() => downloadArtifact(proposal, "quotation")} disabled={busyAction === `${proposal.id}:quotation`} className="h-8 rounded-lg border-[#CBD5E1] px-2.5 text-[#334155]">
        <FileText size={14} /> Quotation
      </Button>
      <Button variant="outline" onClick={() => downloadArtifact(proposal, "ppt")} disabled={busyAction === `${proposal.id}:ppt`} className="h-8 rounded-lg border-[#CBD5E1] px-2.5 text-[#334155]">
        <Presentation size={14} /> PPT
      </Button>
      <Button variant="outline" onClick={() => downloadArtifact(proposal, "excel")} disabled={busyAction === `${proposal.id}:excel`} className="h-8 rounded-lg border-[#CBD5E1] px-2.5 text-[#334155]">
        <FileSpreadsheet size={14} /> Excel
      </Button>
      {busyAction.startsWith(`${proposal.id}:`) && <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#64748B]"><Download size={13} /> Preparing...</span>}
    </div>
  );
}

function ProposalMobileCard({ proposal, busyAction, copyLink, createCampaignFromProposal, downloadArtifact, nav, onOpenDetails, voidProposal }) {
  return (
    <article className="hx-mobile-hoarding-card cursor-pointer" onClick={() => onOpenDetails(proposal)}>
      <ProposalIdentity proposal={proposal} />
      <div className="hx-mobile-hoarding-meta">
        <div>
          <span>Created / Expiry</span>
          <strong>{formatDateTime(proposal.created_at)}</strong>
          <small>Expires {formatDateTime(proposal.proposal_expires_at)}</small>
        </div>
        <div>
          <span>Commercial</span>
          <strong>{inr(proposal.total_cost || 0)}</strong>
          <small>{proposal.total_selected || 0} media · {proposal.open_count || 0} opens</small>
        </div>
      </div>
      <div className="mt-3 rounded-lg bg-slate-50 p-2">
        <ProposalLinks proposal={proposal} copyLink={copyLink} />
      </div>
      {proposal.latest_response && <div className="mt-2 rounded-lg bg-blue-50 p-2"><LatestResponse response={proposal.latest_response} /></div>}
      <div className="mt-3">
        <ProposalActions
          proposal={proposal}
          busyAction={busyAction}
          createCampaignFromProposal={createCampaignFromProposal}
          downloadArtifact={downloadArtifact}
          nav={nav}
          voidProposal={voidProposal}
        />
      </div>
    </article>
  );
}

function Metric({ label, value }) {
  return (
    <div className="min-w-24 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2">
      <div className="text-[11px] font-semibold uppercase text-[#64748B]">{label}</div>
      <div className="mt-1 text-sm font-bold text-[#0F172A]">{value}</div>
    </div>
  );
}
